import { Button, ButtonGroup, SxProps } from "@mui/material";
import React from "react";
import styled from "styled-components";

import { TextControlSize, TextControlStyle } from "../../Form/Utilities/FormDesignConstants";
import { TextLabelIcon } from "../Icon/TextLabelIcon";
import { TextOutlineIcon } from "../Icon/TextOutlineIcon";
import { TextShadedIcon } from "../Icon/TextShadedIcon";
import { TextUnderlineIcon } from "../Icon/TextUnderlineIcon";
import { ObserveState } from "../StateManagement/ObserveState";
import { StateManager } from "../../rule-engine-core/Common/StateManager";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";
import { getPropertyEditorValidationIssues, PropertyEditorValidationTooltip } from "./PropertyEditorValidationTooltip";
import { UILabel } from "../BuildlingBlocks/UILabel";

export interface TextStyleSelectionInterface {
    editObject: object;
    label?: string;
    hint?: string;
    propertyName: string;
    parentDefaultValue?: string | null;
    onChange?: () => void;
    sx?: SxProps;
    error?: string;
    ignoreDesignerValidation?: boolean;
}

const TextStyleButton = styled(Button)`
    && {
        width: 56px;
        height: 46px;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        border: solid 1px ${UIStyleConstants.GRAY_LIGHTER_COLOR};
        border-radius: 4px;

        &:hover {
            background: ${UIStyleConstants.GRAY_LIGHTER_COLOR};
        }

        &.Mui-disabled {
            background: ${UIStyleConstants.GRAY_LIGHTER_COLOR};
        }
    }
`;

export const PropertyEditorTextStyleSelection: React.FC<TextStyleSelectionInterface> = (
    props: TextStyleSelectionInterface,
) => {
    function handleChangeStyle(newStyle: string) {
        if (props.parentDefaultValue && newStyle === props.parentDefaultValue) {
            Reflect.set(props.editObject, props.propertyName, null);
        } else {
            Reflect.set(props.editObject, props.propertyName, newStyle);
        }
        StateManager.propertyChanged(props.editObject, props.propertyName);
        if (props.onChange) props.onChange();
    }

    const value = Reflect.get(props.editObject, props.propertyName) ?? props.parentDefaultValue;

    return (
        <ObserveState
            listenTo={props.editObject}
            properties={[props.propertyName]}
            control={() => {
                let validationIssues = getPropertyEditorValidationIssues(
                    props.editObject,
                    props.propertyName,
                    props.error,
                );

                return (
                    <PropertyEditorValidationTooltip
                        editObject={props.editObject}
                        propertyName={props.propertyName}
                        error={validationIssues}
                    >
                        {props.label && <UILabel>{props.label}</UILabel>}
                        <ButtonGroup
                            size="large"
                            aria-label="large outlined primary button group"
                            style={{
                                height: "40px",
                                width: "220px",
                                marginTop: "10px",
                                border: validationIssues ? "1px solid " + UIStyleConstants.RED_COLOR : undefined,
                                borderRadius: "4px",
                            }}
                            sx={props.sx}
                        >
                            <TextStyleButton
                                onClick={() => {
                                    handleChangeStyle(TextControlStyle.LABELED);
                                }}
                                disabled={value === TextControlStyle.LABELED}
                            >
                                <TextLabelIcon alt={TextControlStyle.LABELED} />
                            </TextStyleButton>
                            <TextStyleButton
                                onClick={() => {
                                    handleChangeStyle(TextControlStyle.OUTLINE);
                                }}
                                disabled={value === TextControlStyle.OUTLINE}
                            >
                                <TextOutlineIcon alt={TextControlStyle.OUTLINE} />
                            </TextStyleButton>
                            <TextStyleButton
                                onClick={() => {
                                    handleChangeStyle(TextControlStyle.SHADED);
                                }}
                                disabled={value === TextControlStyle.SHADED}
                            >
                                <TextShadedIcon alt={TextControlStyle.SHADED} />
                            </TextStyleButton>
                            <TextStyleButton
                                onClick={() => {
                                    handleChangeStyle(TextControlStyle.UNDERLINED);
                                }}
                                disabled={value === TextControlStyle.UNDERLINED}
                            >
                                <TextUnderlineIcon alt={TextControlStyle.UNDERLINED} />
                            </TextStyleButton>
                        </ButtonGroup>
                        {(props.hint || validationIssues) && (
                            <UILabel
                                paddingTop={"8px"}
                                variant={validationIssues ? "error_hint" : "hint"}
                            >
                                {validationIssues?.message ?? props.hint}
                            </UILabel>
                        )}
                    </PropertyEditorValidationTooltip>
                );
            }}
        />
    );
};

const TextSizeButton = styled(Button)`
    && {
        width: 120px;
        height: 46px;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
        border: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};

        &:hover {
            background: ${UIStyleConstants.GRAY_LIGHT_COLOR};
        }

        &.Mui-disabled {
            background: ${UIStyleConstants.GRAY_LIGHT_COLOR};
            color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
        }
    }
`;

export const PropertyEditorTextSizeSelection: React.FC<TextStyleSelectionInterface> = (
    props: TextStyleSelectionInterface,
) => {
    function handleChangeSize(newStyle: string) {
        if (props.parentDefaultValue && newStyle === props.parentDefaultValue) {
            Reflect.set(props.editObject, props.propertyName, null);
        } else {
            Reflect.set(props.editObject, props.propertyName, newStyle);
        }
        StateManager.propertyChanged(props.editObject, props.propertyName);
        if (props.onChange) props.onChange();
    }

    return (
        <ObserveState
            listenTo={props.editObject}
            properties={[props.propertyName]}
            control={() => {
                const value = Reflect.get(props.editObject, props.propertyName) ?? props.parentDefaultValue;
                let validationIssues = getPropertyEditorValidationIssues(
                    props.editObject,
                    props.propertyName,
                    props.error,
                );
                return (
                    <PropertyEditorValidationTooltip
                        editObject={props.editObject}
                        propertyName={props.propertyName}
                        error={validationIssues}
                    >
                        {props.label && <UILabel>{props.label}</UILabel>}
                        <ButtonGroup
                            sx={props.sx}
                            size="large"
                            aria-label="large outlined primary button group"
                            style={{
                                height: "40px",
                                width: "240px",
                                marginTop: "10px",
                                border: validationIssues ? "1px solid " + UIStyleConstants.RED_COLOR : undefined,
                                borderRadius: "4px",
                            }}
                        >
                            <TextSizeButton
                                onClick={() => {
                                    handleChangeSize(TextControlSize.STANDARD);
                                }}
                                style={{ textTransform: "lowercase" }}
                                disabled={value === TextControlSize.STANDARD}
                            >
                                Standard
                            </TextSizeButton>
                            <TextSizeButton
                                onClick={() => {
                                    handleChangeSize(TextControlSize.SMALL);
                                }}
                                style={{ textTransform: "lowercase" }}
                                disabled={value === TextControlSize.SMALL}
                            >
                                Small
                            </TextSizeButton>
                        </ButtonGroup>
                        {(props.hint || validationIssues) && (
                            <UILabel
                                paddingTop={"8px"}
                                variant={validationIssues ? "error_hint" : "hint"}
                            >
                                {validationIssues?.message ?? props.hint}
                            </UILabel>
                        )}
                    </PropertyEditorValidationTooltip>
                );
            }}
        />
    );
};
